import { PopupConfig } from "@/store/types";
import {
  booleanFilter,
  buttons,
  cloneConfig,
  wbcLabConfig,
  White_Blood_Cell_Count
} from "./config";
import { Patient } from "./types";

export function awob() {
  const clonedConfig = cloneConfig();

  clonedConfig.census.gridCardClass = "GridCardOb";

  clonedConfig.census.attending.sparkline[0] = {
    key: "BPSys",
    title: "BP Sys",
    range: 60
  };

  clonedConfig.body.patientDetailBodyComponent = "PatientDetailOB";

  clonedConfig.body.patientDetailBmi = 40;

  clonedConfig.body.bottomPaddingMultiplier = 2;

  const pagingFields = [
    { key: "who", text: "Who" },
    { key: "delay", text: "Delay" },
    {
      key: "stage2or3",
      text: "Stage 2 or 3",
      filter: booleanFilter
    },
    { key: "repage", text: "Re-Page" },
    { key: "repeat", text: "Repeat" },
    { key: "reenable", text: "Snooze" },
    { key: "pageOR", text: "Page OR", filter: booleanFilter }
  ];

  clonedConfig.help.paging.fields = clonedConfig.help.paging.fields.concat(
    pagingFields
  );

  clonedConfig.help.paging.description.isOB = true;

  clonedConfig.help.paging.description.stage2or3 = true;
  clonedConfig.help.paging.description.repage = true;
  clonedConfig.help.paging.description.snooze = true;
  clonedConfig.help.paging.description.pageOR = true;

  [
    "rightKidneyText",
    "leftKidneyText",
    "eblText",
    "heartText",
    "lungRateOnlyText",
    "coughText",
    "heartVolumeText",
    "lungsText",
    "brainText"
  ].forEach(part => {
    clonedConfig.parts[part].textScale = 1.2;
  });

  clonedConfig.parts.ett.configOverride = { riskIds: [] };
  clonedConfig.parts.ett.useOrange = true;

  clonedConfig.parts.heart.strokeLinecapRound = true;
  clonedConfig.parts.uterusGroup.visible = true;
  clonedConfig.parts.spine.visible = true;
  buttons.forEach(part => {
    clonedConfig.body.buttons[part] = {
      visible: false
    };
  });
  clonedConfig.body.buttons.Infusions.visible = true;
  clonedConfig.body.buttons.InfectiousDiseases.visible = true;
  clonedConfig.body.buttons.Allergies.visible = true;
  clonedConfig.body.buttons.OBNotes.visible = true;

  clonedConfig.labConfigs.splice(
    1,
    0,
    {
      title: "UPCr",
      atom: "upc",
      popupName: "Urine_Protein_Creatinine_Ratio"
    },
    {
      title: "FIB",
      atom: "fibrinogen",
      popupName: "Fibrinogen"
    }
  );

  clonedConfig.labConfigs.splice(5, 0, wbcLabConfig);

  const defaultPopupConfig: PopupConfig = {
    graphRanges: [
      { text: "12 hours", hours: 12 },
      { text: "24 hours", hours: 24 },
      { text: "3 days", hours: 24 * 3 },
      { text: "7 days", hours: 24 * 7 },
      { text: "Current Visit", hours: 0 }
    ],
    graphStartingIndex: 0
  };
  clonedConfig.defaultPopupDescription = defaultPopupConfig;

  clonedConfig.popups.White_Blood_Cell_Count = White_Blood_Cell_Count;

  clonedConfig.popups.Urine_Protein_Creatinine_Ratio = {
    component: "GenericRiskAndGraph",
    popupConfig: {
      graphStartingIndex: 2,
      title: "Urine Protein/Creatinine Ratio",
      graphs: [
        {
          source: "UPC",
          suggestedMax: 2
        }
      ]
    }
  };

  clonedConfig.popups.Fibrinogen = {
    component: "GenericRiskAndGraph",
    popupConfig: {
      title: "Fibrinogen",
      graphs: [{ source: "Fibrinogen", suggestedMax: 500 }],
      graphStartingIndex: 2
    }
  };

  // Remove ACT from OB
  clonedConfig.popups.INR.popupConfig.graphs.pop();

  clonedConfig.census.colorLegend = [
    { label: "Triage", caseState: 1 },
    { label: "Antepartum", caseState: 5 },
    { label: "Labor", caseState: 8 },
    { label: "Stage 2 or 3", caseState: 20 },
    { label: "Postpartum", caseState: 12 },
    { label: "Readmit", caseState: 19 },
    { label: "Fetal Loss", caseState: 6 }
  ];

  clonedConfig.census.grid.filterFunction = (patient: Patient) => {
    return !patient.census.roomEmpty;
  };

  clonedConfig.census.LegendDescription = [
    {
      icon: "alertPhys1.png",
      label: "One or more active physiologic alert(s)"
    },
    {
      icon: "alertPhys2.png",
      label: "One or more severe active physiologic alert(s)"
    },
    {
      icon: "alertPhys3.gif",
      label: "Paging for an active alert"
    },
    { icon: "alertETT0.png", label: "Needs current airway examination" },
    { icon: "alertETT1.png", label: "Intubation risk factors" },
    { icon: "alertETT2.png", label: "History of difficult intubation" },
    { icon: "alertPreX.png", label: "Preeclampsia" },
    {
      icon: "alertPreX2.png",
      label: "Severe preeclampsia or on magnesium"
    },
    { icon: "alertTwins.png", label: "Multiple gestation" },
    { icon: "alertCSection.png", label: "History of C/S" },
    {
      icon: "alertEpidural3.png",
      label: "Epidural requested but not in place"
    },
    { icon: "alertEpidural2.png", label: "Epidural in place and pain is high" },
    { icon: "alertEpidural1.png", label: "Epidural in place and pain is low" },
    {
      icon: "alertPlacenta.png",
      label: "Abnormal placentation. Placenta Previa/Accreta/Increta/Percreta"
    },
    {
      icon: "alertPPH1.png",
      label: "At risk for PPH"
    },
    {
      icon: "alertPPH2.png",
      label: "High EBL. 500 cc for normal delivery and 1,000 cc for C/S"
    },
    { icon: "alertHeart.png", label: "Heart Disease" },
    { icon: "alertJehovah.png", label: "Refuses blood transfusions" },
    { icon: "alertIV18.png", label: "IV size" },
    { icon: "note3.png", label: "Care coordination note is present" },
    { icon: "alertSpine1.png", label: "Spine risk factors" },
    { icon: "alertSpine2.png", label: "Epidural hematoma risk" },
    { icon: "alertSettings2.png", label: "Alert limits has been changed" },
    { icon: "alertIsolation.png", label: "Contact or Isolation Precaution" },
    {
      icon: "alertIsolation2.png",
      label: "High Risk Contact or Isolation Precaution"
    },
    { icon: "COVID-19_1.png", label: "COVID-19 test missing or inactive." },
    { icon: "COVID-19_2.png", label: "COVID-19 test result negative." },
    { icon: "COVID-19_3.png", label: "COVID-19 test result pending." },
    {
      icon: "COVID-19_4.png",
      label: "COVID-19 test inactive, previously positive."
    },
    { icon: "COVID-19_5.png", label: "COVID-19 test positive." }
  ];
  clonedConfig.census.legend = [
    {
      label: "Text Alert",
      paths: ["alertPhys1", "alertPhys2"]
    },
    {
      label: "Airway Status",
      paths: ["alertETT0", "alertETT1", "alertETT2"]
    },
    {
      label: "Pre-X",
      paths: ["alertPreX", "alertPreX2"]
    },
    {
      label: "Multiple Gestation",
      paths: ["alertTwins"]
    },
    {
      label: "H/O C/S",
      paths: ["alertCSection"]
    },
    {
      label: "Epidural",
      paths: ["alertEpidural1", "alertEpidural2", "alertEpidural3"]
    },
    {
      label: "Abnormal Placentation",
      paths: ["alertPlacenta"]
    },
    {
      label: "Hemorrhage",
      paths: ["alertPPH1", "alertPPH2"]
    },
    {
      label: "Heart Disease",
      paths: ["alertHeart"]
    },
    {
      label: "Refuses Blood",
      paths: ["alertJehovah"]
    },
    {
      label: "IV Size",
      paths: ["alertIV14", "alertIV18", "alertIV20"]
    },
    {
      label: "Care Note",
      paths: ["note3"]
    },
    {
      label: "Spine/Coagulation",
      paths: ["alertSpine1", "alertSpine2"]
    },
    {
      label: "Precaution",
      paths: ["alertIsolation", "alertIsolation2"]
    },
    {
      label: "COVID-19 Status",
      paths: [
        "COVID-19_1",
        "COVID-19_2",
        "COVID-19_3",
        "COVID-19_4",
        "COVID-19_5"
      ]
    },
    {
      label: "Paging Limits Changed",
      paths: ["alertSettings2"]
    }
  ];

  clonedConfig.body.bodyValues.brain.visible = false;
  clonedConfig.body.bodyValues.lungs.visible = false;

  clonedConfig.census.legend.forEach((item: any) => {
    item.paths = item.paths.map((path: string) => {
      return `${path}.png`;
    });
  });

  clonedConfig.popups.Heart.display.grouped = true;
  clonedConfig.popups.Heart.display.shockIndexGraph = true;
  clonedConfig.popups.Heart.display.pulsePressureOnGraph = true;

  clonedConfig.popups.Brain.display.showObRisks = true;

  return clonedConfig;
}
